/**
 * Shared Overview / Agent Actions failure honesty.
 *
 * Agent Actions already resolves a tool+app signature as:
 *   later success → resolved
 *   last activity older than 6h → stale
 *   known caller-parameter errors → caller_error
 *
 * Overview used to list fix-failed history cards while Score / Active issues
 * ignored that list (backend health_score only deducts a subset of watcher
 * classifications). Both surfaces now classify the same way.
 */

export const STALE_FAILURE_MS = 6 * 60 * 60 * 1000;

export type IssueHonesty = 'open' | 'stale' | 'resolved' | 'dismissed' | 'caller_error';

export type IssueLike = {
  tool_name?: string | null;
  app_id?: string | null;
  status: string;
  classification?: string | null;
  last_failure?: string | null;
  event_time?: string | null;
  diagnosis?: Record<string, unknown> | null;
  error_samples?: string[] | null;
  failure_count?: number | null;
};

export type ClassifiedIssue<T extends IssueLike = IssueLike> = {
  item: T;
  honesty: IssueHonesty;
};

export type ServiceHealthForScore = {
  ok: number;
  degraded: number;
  down: number;
  total: number;
  status: string;
  variant: string;
  label: string;
} | null | undefined;

const CLOSED_STATUSES = new Set(['dismissed', 'resolved', 'deployed', 'fix_pushed']);

const OPEN_FAILURE_STATUSES = new Set([
  'fix_failed',
  'failed',
  'error',
  'active',
  'diagnosed',
  'rate_limited',
  'pending_diagnosis',
  'fixing',
  'running',
]);

export function issueTimestampMs(item: IssueLike): number | null {
  const raw = item.last_failure || item.event_time;
  if (!raw) return null;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : null;
}

export function toolAppKey(item: Pick<IssueLike, 'tool_name' | 'app_id'>): string {
  return `${item.app_id || 'platform'}::${item.tool_name || ''}`;
}

export function issueSearchText(item: IssueLike): string {
  const diagnosis = item.diagnosis || {};
  return [
    item.classification,
    item.status,
    typeof diagnosis.primary_error === 'string' ? diagnosis.primary_error : '',
    typeof diagnosis.issue === 'string' ? diagnosis.issue : '',
    typeof diagnosis.classification === 'string' ? diagnosis.classification : '',
    ...(item.error_samples || []),
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * Mirrors orchestrator `isCallerParameterError` — these are app/agent argument
 * or allowlist mistakes, not platform outages.
 */
export function isCallerErrorIssue(item: IssueLike): boolean {
  if (item.classification === 'caller_error' || item.status === 'caller_error') return true;
  const text = issueSearchText(item);
  if (/Caller error:/i.test(text)) return true;
  const lower = text.toLowerCase();
  return (
    lower.includes('agent_not_allowlisted')
    || lower.includes('agent_archived')
    || /_required\b/.test(lower)
    || lower.includes('missing required')
    || lower.includes('invalid argument')
    || lower.includes('validation failed')
    || lower.includes('is required')
    || lower.includes('input_required')
    || lower.includes('invalid_category')
    || lower.includes('invalid_promotion')
  );
}

export function isClosedIssueStatus(status: string, classification?: string | null): boolean {
  if (classification === 'caller_error') return true;
  return CLOSED_STATUSES.has(String(status || '')) || status === 'skipped';
}

export function isOpenFailureStatus(status: string | null | undefined): boolean {
  return OPEN_FAILURE_STATUSES.has(String(status || ''));
}

export function classifyIssueHonesty(
  item: IssueLike,
  opts?: {
    now?: number;
    latestByKey?: Map<string, { success: boolean; timestamp: number }>;
  },
): IssueHonesty {
  const status = String(item.status || '');
  if (status === 'dismissed') return 'dismissed';
  if (status === 'resolved' || status === 'deployed' || status === 'fix_pushed') return 'resolved';
  if (isCallerErrorIssue(item) || status === 'skipped') return 'caller_error';

  const now = opts?.now ?? Date.now();
  const ts = issueTimestampMs(item);
  const latest = opts?.latestByKey?.get(toolAppKey(item));

  if (latest?.success && ts != null && latest.timestamp > ts) return 'resolved';

  if (ts != null && now - ts >= STALE_FAILURE_MS) {
    if (!latest || now - latest.timestamp >= STALE_FAILURE_MS) return 'stale';
  }

  if (isOpenFailureStatus(status) || status === 'active') return 'open';
  return 'open';
}

export function honestyLabel(honesty: IssueHonesty, status: string, classification?: string | null): string {
  if (honesty === 'dismissed') return 'Resolved — dismissed';
  if (honesty === 'stale') return 'Stale — no recent activity';
  if (honesty === 'resolved') {
    if (status === 'deployed' || status === 'fix_pushed') return 'Resolved';
    return 'Resolved';
  }
  if (honesty === 'caller_error' || status === 'skipped' || classification === 'caller_error') {
    return 'Skipped — caller error';
  }
  if (status === 'fix_failed') return 'Fix failed';
  if (status === 'fixing' || status === 'running') return 'Fix in progress';
  if (status === 'rate_limited') return 'Rate limited';
  if (status === 'diagnosed') return 'Diagnosed';
  return String(status || 'open').replace(/_/g, ' ');
}

export function honestyTone(honesty: IssueHonesty): 'success' | 'warning' | 'danger' {
  if (honesty === 'resolved' || honesty === 'dismissed' || honesty === 'stale') return 'success';
  if (honesty === 'caller_error') return 'warning';
  return 'danger';
}

export function mergeIssueCards<T extends IssueLike>(activeFailures: T[], history: T[]): T[] {
  const byKey = new Map<string, T>();

  for (const item of history) {
    const key = toolAppKey(item);
    const existing = byKey.get(key);
    if (!existing || (issueTimestampMs(item) || 0) >= (issueTimestampMs(existing) || 0)) {
      byKey.set(key, item);
    }
  }

  for (const item of activeFailures) {
    const key = toolAppKey(item);
    const existing = byKey.get(key);
    if (!existing || (issueTimestampMs(item) || 0) >= (issueTimestampMs(existing) || 0)) {
      byKey.set(key, existing ? { ...existing, ...item } : item);
    }
  }

  return [...byKey.values()];
}

export function classifyIssueCards<T extends IssueLike>(
  items: T[],
  now = Date.now(),
): ClassifiedIssue<T>[] {
  const latestByKey = new Map<string, { success: boolean; timestamp: number }>();
  for (const item of items) {
    const ts = issueTimestampMs(item) ?? 0;
    const key = toolAppKey(item);
    const success = classifyClosedAsSuccess(item);
    const prev = latestByKey.get(key);
    if (!prev || ts > prev.timestamp) {
      latestByKey.set(key, { success, timestamp: ts });
    }
  }

  return items
    .map((item) => ({
      item,
      honesty: classifyIssueHonesty(item, { now, latestByKey }),
    }))
    .sort((a, b) => (issueTimestampMs(b.item) || 0) - (issueTimestampMs(a.item) || 0));
}

function classifyClosedAsSuccess(item: IssueLike): boolean {
  const status = String(item.status || '');
  return status === 'resolved' || status === 'deployed' || status === 'fix_pushed';
}

/**
 * Open (non-stale, non-resolved, non-dismissed, non-caller-error) cards
 * cannot claim a perfect score. Penalty matches auto-heal's per-failure
 * deduction shape so 1–3 cards read as degraded, not 99/100 noise.
 */
export function deriveHonestyScore(baseScore: number, openIssueCount: number): number {
  const base = Math.max(0, Math.min(100, Math.round(baseScore)));
  if (openIssueCount <= 0) return base;
  const penalty = Math.min(50, 20 * openIssueCount);
  return Math.max(0, Math.min(base, 100 - penalty));
}

export type PlatformHealthHonesty = {
  cards: ClassifiedIssue<IssueLike>[];
  openIssueCount: number;
  callerIssueCount: number;
  serviceIssueCount: number;
  activeIssueCount: number;
  score: number;
};

export function derivePlatformHealthHonesty(input: {
  activeFailures?: IssueLike[];
  history?: IssueLike[];
  backendScore?: number;
  serviceHealth?: ServiceHealthForScore;
  now?: number;
}): PlatformHealthHonesty {
  const activeFailures = input.activeFailures || [];
  const history = input.history || [];
  const merged = mergeIssueCards(activeFailures, history);
  const cards = classifyIssueCards(merged, input.now);
  const openCards = cards.filter((c) => c.honesty === 'open');
  const callerIssueCount = cards.filter((c) => c.honesty === 'caller_error').length;
  const down = input.serviceHealth?.down ?? 0;
  const degraded = input.serviceHealth?.degraded ?? 0;
  const serviceIssueCount = down + degraded;
  const openIssueCount = openCards.length;
  const activeIssueCount = openIssueCount + serviceIssueCount;

  let serviceScore = 100;
  if (input.serviceHealth && input.serviceHealth.total > 0) {
    serviceScore = Math.round((100 * input.serviceHealth.ok) / input.serviceHealth.total);
  } else if (input.serviceHealth && input.serviceHealth.status && input.serviceHealth.status !== 'Healthy') {
    serviceScore = input.serviceHealth.status === 'Unknown' ? 100 : 0;
  }

  const base = Math.min(input.backendScore ?? 100, serviceScore);
  const score = deriveHonestyScore(base, activeIssueCount);

  return {
    cards,
    openIssueCount,
    callerIssueCount,
    serviceIssueCount,
    activeIssueCount,
    score,
  };
}
